import I18n from 'I18n';
// @ts-expect-error untyped module I18n/utils/localeComparator
import localeComparator from 'I18n/utils/localeComparator';
import findAssociationDefinitions from 'automation-ui-associations/utils/findAssociationDefinitions';
import { assocDefToSpec } from 'automation-ui-associations/utils/associationDefinitionsUtils';
import { ONE_TO_MANY } from 'automation-ui-associations/constants/AssociationCardinality';
import { USER_DEFINED } from 'automation-ui-associations/constants/AssociationCategory';
import getAssociationLabel from 'automation-ui-associations/utils/getAssociationLabel';
import getAssociationLabelHelp from 'automation-ui-associations/utils/getAssociationLabelHelp';
import ObjectType from 'automation-ui-references/object-types/ObjectType';
import { getIsParentChildCompanyAssociation } from 'automation-ui-associations/utils/sameObjectAssociations';
import { getFormattedMessageWithObjectTypeName } from 'automation-ui-i18n/i18nUtils';
function getDisplayOrderForHubSpotDefinedAssociation({
  hasAllAssociatedObjects,
  isPrimary,
  isInversePrimary
}) {
  if (hasAllAssociatedObjects) {
    return -3;
  } else if (isPrimary) {
    return -2;
  } else if (isInversePrimary) {
    return -1;
  } else {
    return 0;
  }
}
function compareHubSpotDefinedAssociations(associationA, associationB) {
  return getDisplayOrderForHubSpotDefinedAssociation(associationA) - getDisplayOrderForHubSpotDefinedAssociation(associationB);
}
export default function getAssociationLabelOptions({
  fromObjectTypeId,
  fromCardinality,
  toObjectTypeId,
  toObjectTypeDefinition,
  associationDefinitionsQuery,
  allAssociatedObjectsLangKey = 'automation-ui-associations.flexible-associations.default-label_cob',
  hasReachedAssociationLimit,
  value,
  supportsNonFlexibleAssociations,
  shouldUseAlternateAllAssociationsLabel = false,
  disallowOneToMany
}) {
  const associationDefinitions = findAssociationDefinitions(fromObjectTypeId, toObjectTypeId, associationDefinitionsQuery);

  /**
   * If we are only looking for flexible associations, it's not
   *  safe to assume that there will always be an unlabeled
   * association
   * (i.e. an association with `hasAllAssociatedObjects=true`).
   * Only those with ONE_TO_MANY in BOTH cardinalities will have an
   * unlabeled association.
   */
  const hasOneToManyCardinalityInBothCardinalities = !supportsNonFlexibleAssociations && associationDefinitions.some(definition => definition.cardinality === ONE_TO_MANY && definition.inverseCardinality === ONE_TO_MANY);
  const containsFlexibleAssociationDefinitions = associationDefinitions.some(definition => definition.hasAllAssociatedObjects);
  if (!containsFlexibleAssociationDefinitions && hasOneToManyCardinalityInBothCardinalities) {
    return [];
  }
  let options = [];
  let customLabelOptions = [];
  const toObjectType = new ObjectType(toObjectTypeDefinition);
  associationDefinitions.forEach(associationDefinition => {
    const text = getAssociationLabel({
      associationDefinition,
      toObjectTypeDefinition: toObjectType,
      allAssociatedObjectsLangKey: shouldUseAlternateAllAssociationsLabel ? 'automation-ui-associations.flexible-associations.alternate-default-label_cob' : allAssociatedObjectsLangKey
    });
    const help = getAssociationLabelHelp({
      associationDefinition,
      shouldUseAlternateAllAssociationsLabel
    });
    const associationSpec = assocDefToSpec(associationDefinition);
    const associationTypeId = associationSpec.getFullAssociationTypeId();
    const targetCardinalityDisallowed = disallowOneToMany && associationDefinition.cardinality === ONE_TO_MANY;
    // If we're copying to this association from another, forbid copying
    // ONE_TO_MANY to ONE_TO_ONE by skiping any ONE_TO_ONE options as it is not
    // possible to write multiple associations to them.
    const sourceCardinalityDisallowed = fromCardinality === 'ONE_TO_MANY' && associationDefinition.cardinality === 'ONE_TO_ONE';
    const selectionWouldExceedLimit = hasReachedAssociationLimit && value && !value.includes(associationTypeId || '');
    const shouldDisableOptions = targetCardinalityDisallowed || sourceCardinalityDisallowed || selectionWouldExceedLimit;
    let disabledReason;
    if (targetCardinalityDisallowed) {
      disabledReason = getFormattedMessageWithObjectTypeName({
        objectTypeId: toObjectTypeDefinition.objectTypeId,
        baseLangKey: 'automation-ui-associations.disabled-due-to-cardinality_cob',
        objectTypeDefinition: toObjectType
      });
    } else if (sourceCardinalityDisallowed) {
      disabledReason = getFormattedMessageWithObjectTypeName({
        objectTypeId: toObjectTypeDefinition.objectTypeId,
        baseLangKey: 'automation-ui-associations.disabled-due-to-label-cardinality_cob',
        objectTypeDefinition: toObjectType
      });
    } else if (selectionWouldExceedLimit) {
      disabledReason = I18n.text('automation-ui-associations.disabled-due-to-association-limit');
    }
    const option = {
      text,
      help,
      associationSpec,
      value: associationTypeId,
      hasAllAssociatedObjects: associationDefinition.hasAllAssociatedObjects,
      associationDefinition,
      // for filters tracking
      isPublicPrimary: associationDefinition.isPrimary,
      isWildcard: associationDefinition.hasAllAssociatedObjects,
      disabled: shouldDisableOptions,
      disabledReason: shouldDisableOptions ? disabledReason : undefined
    };
    const isParentChildCompanyAssociation = getIsParentChildCompanyAssociation({
      associationDefinition
    });

    // Only show HubSpot-defined associations flagged as part of the flexible
    // association ecosystem unless supportsNonFlexibleAssociations
    // is true.
    if (associationDefinition.hasAllAssociatedObjects || associationDefinition.isPrimary || associationDefinition.isInversePrimary || supportsNonFlexibleAssociations && associationDefinition.category !== USER_DEFINED) {
      options.push(option);
    } else if (option.text && (associationDefinition.category === USER_DEFINED || isParentChildCompanyAssociation)) {
      customLabelOptions.push(option);
    }
  });
  options = options.sort(compareHubSpotDefinedAssociations);
  customLabelOptions = customLabelOptions.sort((def1, def2) => localeComparator(def1.text, def2.text));
  if (options.length === 0) {
    return customLabelOptions;
  }
  if (customLabelOptions.length > 0) {
    options.push({
      text: I18n.text('automation-ui-associations.flexible-associations.labeled-options-group-title'),
      options: customLabelOptions
    });
  }
  return options;
}